import { useEffect, useState } from 'react';
import { Megaphone, X } from 'lucide-react';

interface AnnouncementBannerProps {
  announcementText?: string;
  announcementBadge?: string;
}

export default function AnnouncementBanner({
  announcementText,
  announcementBadge = 'Announcement'
}: AnnouncementBannerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Parse messages from the announcement string
  const messages = (announcementText || '')
    .split(';')
    .map((msg) => msg.trim())
    .filter(Boolean);

  // Unique key to detect if the banner content has been modified by the owner
  const currentTextHash = announcementText || '';

  // Check if this specific announcement text has been dismissed in the current session
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const dismissedHash = sessionStorage.getItem('ninpo:dismissed_announcement');
      if (dismissedHash === currentTextHash) {
        setIsDismissed(true);
      } else {
        setIsDismissed(false);
      }
    }
  }, [currentTextHash]);

  // Reset index if it becomes out of bounds (e.g., owner decreases number of messages)
  useEffect(() => {
    if (currentIndex >= messages.length) {
      setCurrentIndex(0);
    }
  }, [messages.length, currentIndex]);

  // Handle message rotation if multiple messages are present
  useEffect(() => {
    if (messages.length <= 1) {
      setCurrentIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setIsFading(true);
      // Wait for fade-out to complete before changing the index
      const timeout = setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % messages.length);
        setIsFading(false);
      }, 300);

      return () => clearTimeout(timeout);
    }, 5000); // Transition every 5 seconds

    return () => clearInterval(interval);
  }, [messages.length]);

  if (!announcementText || messages.length === 0 || isDismissed) {
    return null;
  }

  const handleDismiss = () => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('ninpo:dismissed_announcement', currentTextHash);
    }
    setIsDismissed(true);
  };

  const activeMessage = messages[currentIndex] || '';

  return (
    <div
      role="complementary"
      className="bg-black/90 border-b border-ninpo-lime/20 backdrop-blur-md sticky top-0 z-[9999] shadow-[0_1px_15px_rgba(141,251,0,0.12)] flex items-center justify-between px-6 py-2.5 transition-all"
    >
      {/* Visual background accents */}
      <div className="absolute inset-0 bg-gradient-to-r from-ninpo-lime/5 via-transparent to-ninpo-lime/5 pointer-events-none" />

      {/* Left spacer/container for symmetry */}
      <div className="flex items-center gap-3 relative z-10">
        <span className="flex h-2 w-2 relative">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-ninpo-lime opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-ninpo-lime"></span>
        </span>
        <div className="hidden sm:flex items-center gap-1.5 bg-ninpo-lime/10 border border-ninpo-lime/30 px-2 py-0.5 rounded text-[9px] font-black text-ninpo-lime uppercase tracking-widest shadow-[0_0_8px_rgba(141,251,0,0.15)]">
          {announcementBadge}
        </div>
      </div>

      {/* Message center */}
      <div className="flex-1 flex justify-center items-center px-4 relative z-10 overflow-hidden">
        <div
          className={`flex items-center gap-2.5 text-center transition-opacity duration-300 ${
            isFading ? 'opacity-0' : 'opacity-100'
          }`}
        >
          <Megaphone className="w-4 h-4 text-ninpo-lime shrink-0 animate-pulse drop-shadow-[0_0_6px_rgba(141,251,0,0.4)]" />
          <span className="text-xs sm:text-sm font-bold tracking-wide text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.1)]">
            {activeMessage}
          </span>
        </div>
      </div>

      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        aria-label="Dismiss banner"
        className="p-1 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-all duration-200 relative z-10 shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
